/**
 * IdentityReviewQueue
 * Coláiste Lurgan Management System - resolve ambiguous returning-student
 * matches flagged by the Claru deposit webhook (or the historical backfill).
 *
 * One review "in focus" at a time — comparing two full student records is
 * too dense for a table, so this shows the flagged student against each
 * candidate as cards, with matching fields highlighted, and a single clear
 * action per candidate.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useToast } from '../../hooks/use-toast';
import { UserSearch, CheckCircle2, XCircle, Loader2, PartyPopper, SkipForward, ChevronDown, ChevronUp } from 'lucide-react';

const QUERY_KEY = ['identity-reviews'];

// Mirrors the backend's scoring fields exactly (students/identity_matching.py
// find_candidate_suggestions) — first name, last name, birthday, parent
// email. Parent phone isn't part of the backend score, so it's excluded
// here too even though it's shown on the card.
function candidateScore(candidate, flagged) {
  if (!candidate || !flagged) return 0;
  return (
    (fieldsMatch(candidate.first_name, flagged.first_name) ? 1 : 0) +
    (fieldsMatch(candidate.last_name, flagged.last_name) ? 1 : 0) +
    (candidate.birthday === flagged.birthday ? 1 : 0) +
    (fieldsMatch(candidate.parent_email, flagged.parent_email) ? 1 : 0)
  );
}

function topCandidateScore(review) {
  const candidate = review?.candidates?.[0];
  return candidate ? candidateScore(candidate, review.student) : 0;
}

function formatDate(value) {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('en-IE');
}

function fieldsMatch(a, b) {
  if (!a || !b) return false;
  return String(a).trim().toLowerCase() === String(b).trim().toLowerCase();
}

function Field({ label, value, matches }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={matches ? 'font-medium text-emerald-600 dark:text-emerald-400 flex items-center gap-1' : ''}>
        {matches && <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />}
        <span>{value || '—'}</span>
      </div>
    </div>
  );
}

function StudentSummary({ student }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
      <Field label="Name" value={`${student.first_name} ${student.last_name}`} />
      <Field label="DOB" value={formatDate(student.birthday)} />
      <Field label="Parent" value={student.parent_name} />
      <Field label="Parent email" value={student.parent_email} />
      <Field label="Parent phone" value={student.parent_phone} />
      <Field label="Course" value={`${student.course_name || '—'} (${student.school_year_name || '—'})`} />
    </div>
  );
}

function CandidateCard({ candidate, flagged, onConfirm, isConfirming, score, index, isBest }) {
  return (
    <Card className={isBest ? 'border-emerald-400 dark:border-emerald-600' : 'border-dashed'}>
      <CardContent className="pt-6 space-y-4">
        <div className="flex items-center gap-2">
          <span className="flex items-center justify-center h-5 w-5 rounded-full bg-slate-100 dark:bg-slate-800 text-xs font-medium text-slate-600 dark:text-slate-300 shrink-0">
            {index}
          </span>
          <Badge variant="outline">{score}/4 match</Badge>
          {isBest && <Badge variant="secondary">Best match</Badge>}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <Field
            label="Name"
            value={`${candidate.first_name} ${candidate.last_name}`}
            matches={fieldsMatch(candidate.first_name, flagged.first_name) && fieldsMatch(candidate.last_name, flagged.last_name)}
          />
          <Field
            label="DOB"
            value={formatDate(candidate.birthday)}
            matches={candidate.birthday === flagged.birthday}
          />
          <Field label="Parent" value={candidate.parent_name} />
          <Field
            label="Parent email"
            value={candidate.parent_email}
            matches={fieldsMatch(candidate.parent_email, flagged.parent_email)}
          />
          <Field
            label="Parent phone"
            value={candidate.parent_phone}
            matches={fieldsMatch(candidate.parent_phone, flagged.parent_phone)}
          />
          <Field label="Course" value={`${candidate.course_name || '—'} (${candidate.school_year_name || '—'})`} />
        </div>
        <div className="flex items-center justify-between">
          <Badge variant="outline">{candidate.status}</Badge>
          <Button size="sm" onClick={onConfirm} disabled={isConfirming}>
            {isConfirming ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <CheckCircle2 className="h-4 w-4 mr-1.5" />}
            This is the same person
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export function IdentityReviewQueue() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: reviews, isLoading } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: api.identityReviews.getAll,
    staleTime: 30 * 1000,
  });

  // Reviews skipped this sitting, in the order skipped (most recent last) —
  // pure client-side reordering, not persisted, so the queue always fetches
  // and resolves against the real (unskipped) order.
  const [skipOrder, setSkipOrder] = React.useState([]);

  // Collapsed secondary candidates are expanded on demand per review.
  const [showAllCandidates, setShowAllCandidates] = React.useState(false);

  const orderedReviews = React.useMemo(() => {
    const list = reviews || [];
    // Confidence-sorted: reviews with an obvious best candidate surface
    // first, so staff clear the easy ones before the genuinely ambiguous
    // ones. Stable sort keeps backend (created_at) order among ties.
    const sorted = [...list].sort((a, b) => topCandidateScore(b) - topCandidateScore(a));
    if (skipOrder.length === 0) return sorted;
    const skippedSet = new Set(skipOrder);
    const notSkipped = sorted.filter((r) => !skippedSet.has(r.uuid));
    const skipped = skipOrder.map((uuid) => sorted.find((r) => r.uuid === uuid)).filter(Boolean);
    return [...notSkipped, ...skipped];
  }, [reviews, skipOrder]);

  const removeFromCache = (uuid) => {
    queryClient.setQueryData(QUERY_KEY, (prev) => (prev || []).filter((r) => r.uuid !== uuid));
  };

  const confirmMutation = useMutation({
    mutationFn: ({ uuid, candidateUuid }) => api.identityReviews.confirm(uuid, candidateUuid),
    onSuccess: (_data, { uuid }) => {
      removeFromCache(uuid);
      toast({ title: 'Linked', description: 'Records linked as the same student.', variant: 'success' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to link records. Please try again.', variant: 'destructive' });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: (uuid) => api.identityReviews.reject(uuid),
    onSuccess: (_data, uuid) => {
      removeFromCache(uuid);
      toast({ title: 'Resolved', description: 'Marked as not a match.', variant: 'success' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to resolve. Please try again.', variant: 'destructive' });
    },
  });

  const current = orderedReviews[0];
  const remaining = reviews?.length ?? 0;
  const canSkip = remaining > 1;
  const isMutating = confirmMutation.isPending || rejectMutation.isPending;

  // Collapsing only resets when the review in focus actually changes.
  React.useEffect(() => {
    setShowAllCandidates(false);
  }, [current?.uuid]);

  const handleSkip = React.useCallback(() => {
    if (!current || !canSkip) return;
    setSkipOrder((prev) => [...prev.filter((uuid) => uuid !== current.uuid), current.uuid]);
  }, [current, canSkip]);

  const handleReject = React.useCallback(() => {
    if (!current || rejectMutation.isPending) return;
    rejectMutation.mutate(current.uuid);
  }, [current, rejectMutation]);

  const handleConfirmIndex = React.useCallback(
    (position) => {
      if (!current || confirmMutation.isPending) return;
      const candidate = current.candidates[position];
      if (!candidate) return;
      confirmMutation.mutate({ uuid: current.uuid, candidateUuid: candidate.uuid });
    },
    [current, confirmMutation]
  );

  // Keyboard shortcuts: 1-5 confirms that candidate by position (even if its
  // card is currently collapsed), N = not a match, S / → = skip. Ignored
  // while a mutation is in flight or a modifier key is held, to avoid
  // double-firing or clashing with browser shortcuts.
  React.useEffect(() => {
    if (!current) return undefined;

    const handler = (event) => {
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      if (isMutating) return;

      if (event.key >= '1' && event.key <= '9') {
        handleConfirmIndex(Number(event.key) - 1);
        return;
      }
      if (event.key === 'n' || event.key === 'N') {
        handleReject();
        return;
      }
      if (event.key === 's' || event.key === 'S' || event.key === 'ArrowRight') {
        handleSkip();
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [current, isMutating, handleConfirmIndex, handleReject, handleSkip]);

  return (
    <div className="p-6 space-y-6 max-w-3xl">
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="p-2 bg-slate-100 dark:bg-slate-800 rounded-lg mt-0.5">
          <UserSearch className="w-5 h-5 text-slate-600 dark:text-slate-300" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">{t('identityReview.title', 'Identity Review Queue')}</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            {t('identityReview.description', 'Ambiguous returning-student matches from Claru, flagged for a staff decision.')}
          </p>
        </div>
        {!isLoading && remaining > 0 && (
          <Badge variant="secondary" className="ml-auto self-center">
            {t('identityReview.remaining', '{{count}} pending', { count: remaining })}
          </Badge>
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          {t('common.loading', 'Loading...')}
        </div>
      ) : !current ? (
        <Card>
          <CardContent className="pt-10 pb-10 flex flex-col items-center gap-3 text-center">
            <PartyPopper className="h-10 w-10 text-emerald-500" />
            <p className="font-medium">{t('identityReview.empty', "All caught up — nothing pending review.")}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">{t('identityReview.flaggedStudent', 'Newly created student')}</CardTitle>
              <CardDescription>{t('identityReview.flaggedHint', 'Flagged {{date}} — partially matches the record(s) below.', { date: formatDate(current.created_at) })}</CardDescription>
            </CardHeader>
            <CardContent>
              <StudentSummary student={current.student} />
            </CardContent>
          </Card>

          <div>
            <h3 className="text-sm font-medium mb-2">{t('identityReview.possibleMatches', 'Possible matches')}</h3>
            {(() => {
              const scored = current.candidates.map((candidate) => ({
                candidate,
                score: candidateScore(candidate, current.student),
              }));
              const isTie = scored.length > 1 && scored[1].score === scored[0].score;
              const expandAll = showAllCandidates || isTie;
              const visible = expandAll ? scored : scored.slice(0, 1);
              const hiddenCount = scored.length - visible.length;

              return (
                <div className="space-y-3">
                  {visible.map(({ candidate, score }, i) => (
                    <CandidateCard
                      key={candidate.uuid}
                      candidate={candidate}
                      flagged={current.student}
                      score={score}
                      index={i + 1}
                      isBest={i === 0 && !isTie}
                      isConfirming={confirmMutation.isPending}
                      onConfirm={() => handleConfirmIndex(i)}
                    />
                  ))}
                  {hiddenCount > 0 && (
                    <Button variant="ghost" size="sm" onClick={() => setShowAllCandidates(true)} className="w-full">
                      <ChevronDown className="h-4 w-4 mr-1.5" />
                      {t('identityReview.showOthers', 'Show {{count}} other possible matches', { count: hiddenCount })}
                    </Button>
                  )}
                  {expandAll && !isTie && scored.length > 1 && (
                    <Button variant="ghost" size="sm" onClick={() => setShowAllCandidates(false)} className="w-full">
                      <ChevronUp className="h-4 w-4 mr-1.5" />
                      {t('identityReview.hideOthers', 'Hide other matches')}
                    </Button>
                  )}
                </div>
              );
            })()}
          </div>

          <div className="flex gap-3">
            <Button
              variant="ghost"
              className="flex-1"
              onClick={handleSkip}
              disabled={!canSkip}
              title={!canSkip ? t('identityReview.skipDisabled', 'Nothing else to skip to') : undefined}
            >
              <SkipForward className="h-4 w-4 mr-1.5" />
              {t('identityReview.skip', 'Skip for now')}
            </Button>
            <Button
              variant="outline"
              className="flex-1"
              onClick={handleReject}
              disabled={rejectMutation.isPending}
            >
              {rejectMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <XCircle className="h-4 w-4 mr-1.5" />}
              {t('identityReview.notAMatch', 'None of these — not a match')}
            </Button>
          </div>

          <p className="text-xs text-muted-foreground text-center">
            {t('identityReview.shortcutHint', 'Shortcuts: 1–5 confirm a match · N not a match · S skip')}
          </p>
        </div>
      )}
    </div>
  );
}

export default IdentityReviewQueue;
